import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  output,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { AddressInfo } from '@moxi.gmbh/moxi-typescriptmodels';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonHttpService } from '@shared/data';
import { FieldValidationMsgPipe } from '@shared/pipes';
import { NotificationService } from '@shared/services';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'moxi-place-autocomplete',
  imports: [
    CommonModule,
    FormsModule,

    ReactiveFormsModule,
    FieldValidationMsgPipe,

    TranslatePipe,

    // Ant Design
    NzInputModule,
    NzIconModule,
    NzFormModule
  ],
  templateUrl: './place-autocomplete.component.html'
})
export class PlaceAutocompleteComponent implements OnInit, OnDestroy {
  private readonly ngZone = inject(NgZone);
  private readonly commonService = inject(CommonHttpService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationService = inject(NotificationService);

  formGroup = input<FormGroup | undefined>();
  searchTerm = input<string | undefined>();
  placeholder = input<string>('Search an address...');
  required = input(false);

  setFormValuesEffect = effect(() => this.setValues());
  handleAutocompleteEffect = effect(() => {
    if (this.autocompleteField()) {
      untracked(() => this.handleAutocomplete());
    }
  });

  placeChanged = output<AddressInfo | undefined>();

  autocomplete: google.maps.places.Autocomplete | undefined;
  placeFormGroup = new FormGroup({ autocomplete: new FormControl() });
  selectedAddress: AddressInfo | undefined;

  autocompleteField = viewChild<ElementRef>('autocomplete');

  ngOnInit(): void {
    this.formGroup()?.setControl(
      'autocomplete',
      new FormControl(this.searchTerm(), { validators: this.validateAddress() })
    );

    if (this.required()) {
      this.formGroup()?.controls.autocomplete?.addValidators(
        Validators.required
      );
    }

    this.formGroup()
      ?.controls.autocomplete.valueChanges.pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => value || this.onClearField());
  }

  ngOnDestroy(): void {
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  }

  onClearField(): void {
    this.autocompleteField().nativeElement.value = null;
    this.formGroup()?.controls.autocomplete.setValue(null);
    this.placeChanged.emit(undefined);
    this.selectedAddress = undefined;
  }

  handleAutocomplete(): void {
    if (typeof google !== 'object' || typeof google.maps !== 'object') {
      return;
    }

    const options: google.maps.places.AutocompleteOptions = {
      componentRestrictions: { country: 'de' },
      fields: ['address_components', 'geometry', 'name'],
      strictBounds: false
      // types :["address"]
    };

    this.autocomplete = new google.maps.places.Autocomplete(
      this.autocompleteField().nativeElement,
      options
    );

    this.autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = this.autocomplete?.getPlace();
        const number = this.parseAddress(place, 'street_number');
        if (!number) {
          this.notificationService.warning(
            'shared.address.invalidAddressError'
          );

          this.onClearField();
          return;
        }

        const geolocation = {
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng()
        };
        this.commonService.getDistrict(geolocation).subscribe(info => {
          if (!info?.nutsCode) {
            this.notificationService.warning(
              'shared.address.districtNotFoundError'
            );

            this.onClearField();
            return;
          }

          const street = this.parseAddress(place, 'route');
          const city = this.parseAddress(place, 'locality');
          const zipCode = this.parseAddress(place, 'postal_code');
          const placeName = place?.name?.startsWith(street)
            ? null
            : place?.name;

          const result: AddressInfo = {
            city,
            districtNutsCode: info.nutsCode,
            geoLocation: geolocation,
            placeName,
            number,
            street,
            zipCode
          };

          this.selectedAddress = result;
          this.placeChanged.emit(result);
        });
      });
    });
  }

  private parseAddress(
    place: google.maps.places.PlaceResult,
    name: string
  ): string {
    return place?.address_components?.find(address =>
      address?.types.includes(name)
    )?.long_name;
  }

  private readonly validateAddress =
    (): ValidatorFn => (control: FormControl) =>
      control?.value && !this.selectedAddress?.number
        ? { invalidAddress: true }
        : null;

  private setValues(): void {
    const form = this.formGroup();

    form?.get('autocomplete')?.setValue(this.searchTerm());
    this.selectedAddress = { ...(form.getRawValue() as AddressInfo) };
  }
}
